import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'

/**
 * Export dashboard sections (elements marked with [data-pdf-section]) to a
 * landscape A4 PDF. Sections are packed onto pages top-to-bottom and are
 * never split across a page break — if a section doesn't fit in the
 * remaining space it starts on a new page. Sections taller than a full
 * page are scaled down to fit a single page.
 *
 * @param {string} devLabel - e.g. "All" or "TTR, SSH" for the filename/title
 * @returns {Promise<void>} resolves when the download has been triggered
 */
export async function exportDashboardPdf(devLabel) {
  const sections = Array.from(document.querySelectorAll('[data-pdf-section]'))
  if (!sections.length) throw new Error('No sections found to export')

  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' })
  const PAGE_W = 297
  const PAGE_H = 210
  const MARGIN = 8
  const usableW = PAGE_W - MARGIN * 2
  const usableH = PAGE_H - MARGIN * 2

  // Dark navy page background (matches the dashboard's dashboard-bg colour)
  const BG = [15, 39, 68] // #0f2744
  const paintPageBackground = () => {
    pdf.setFillColor(BG[0], BG[1], BG[2])
    pdf.rect(0, 0, PAGE_W, PAGE_H, 'F')
  }
  paintPageBackground()

  // Title line on the first page
  const now = new Date()
  const pad = (n) => String(n).padStart(2, '0')
  const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}-${pad(now.getMinutes())}`
  const title = `Dashboard (${devLabel}) — ${now.toLocaleString()}`
  pdf.setFontSize(11)
  pdf.setTextColor(219, 234, 254)
  pdf.text(title, MARGIN, MARGIN + 3)

  let y = MARGIN + 8 // below the title
  const GAP = 4 // mm gap between sections

  // Browsers cap canvas dimensions (~32k px per side); stay well below so
  // very tall sections (e.g. long tables with all developments) still capture
  const MAX_CANVAS_DIM = 16000
  const failedSections = []

  for (const el of sections) {
    const rect = el.getBoundingClientRect()
    const scale = Math.min(
      2,
      MAX_CANVAS_DIM / Math.max(rect.height, 1),
      MAX_CANVAS_DIM / Math.max(rect.width, 1)
    )

    let canvas
    try {
      canvas = await html2canvas(el, {
        scale,
        backgroundColor: '#ffffff',
        logging: false
      })
    } catch (err) {
      console.error('PDF export: failed to capture section, skipping', el, err)
      failedSections.push(el)
      continue
    }
    if (!canvas || !canvas.width || !canvas.height) {
      failedSections.push(el)
      continue
    }

    // Scale to usable width, preserving aspect ratio
    let imgW = usableW
    let imgH = (canvas.height * imgW) / canvas.width

    // Long tables (marked data-pdf-split): slice across pages at full width
    // rather than shrinking to fit — keeps text readable
    if (el.hasAttribute('data-pdf-split') && imgH > usableH) {
      const pxPerMm = canvas.width / usableW

      // Row boundaries (bottom edge of each table row) in canvas pixels,
      // so page cuts land between rows rather than through them
      const elTop = el.getBoundingClientRect().top
      const rowBoundaries = Array.from(el.querySelectorAll('thead tr, tbody tr, tfoot tr'))
        .map(tr => Math.round((tr.getBoundingClientRect().bottom - elTop) * scale))
        .filter(b => b > 0 && b < canvas.height)
        .sort((a, b) => a - b)

      let offsetPx = 0

      while (offsetPx < canvas.height) {
        let remainingMm = PAGE_H - MARGIN - y
        // Not enough useful space left on this page → start a new one
        if (remainingMm < 30) {
          pdf.addPage()
          paintPageBackground()
          y = MARGIN
          remainingMm = usableH
        }

        const maxChunkPxH = Math.min(canvas.height - offsetPx, Math.floor(remainingMm * pxPerMm))

        // Snap the cut to the last row boundary that fits within this page,
        // unless we're on the final chunk or no boundary fits (oversized row)
        let chunkPxH = maxChunkPxH
        if (offsetPx + maxChunkPxH < canvas.height) {
          const cutLimit = offsetPx + maxChunkPxH
          const snap = rowBoundaries.filter(b => b > offsetPx && b <= cutLimit).pop()
          if (snap) chunkPxH = snap - offsetPx
        }
        const chunkCanvas = document.createElement('canvas')
        chunkCanvas.width = canvas.width
        chunkCanvas.height = chunkPxH
        const ctx = chunkCanvas.getContext('2d')
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, chunkCanvas.width, chunkCanvas.height)
        ctx.drawImage(canvas, 0, offsetPx, canvas.width, chunkPxH, 0, 0, canvas.width, chunkPxH)

        const chunkMmH = chunkPxH / pxPerMm
        pdf.addImage(chunkCanvas.toDataURL('image/jpeg', 0.92), 'JPEG', MARGIN, y, usableW, chunkMmH)
        y += chunkMmH + GAP
        offsetPx += chunkPxH
      }
      continue
    }

    // Graphics taller than a full page: scale to fit one page (centred)
    if (imgH > usableH) {
      imgH = usableH
      imgW = (canvas.width * imgH) / canvas.height
    }

    // Doesn't fit in remaining space on this page → new page (never split)
    if (y + imgH > PAGE_H - MARGIN && y > MARGIN + 8) {
      pdf.addPage()
      paintPageBackground()
      y = MARGIN
    }

    const x = MARGIN + (usableW - imgW) / 2
    pdf.addImage(canvas.toDataURL('image/jpeg', 0.92), 'JPEG', x, y, imgW, imgH)
    y += imgH + GAP
  }

  if (failedSections.length === sections.length) {
    throw new Error('All sections failed to capture — no PDF generated')
  }

  pdf.save(`Dashboard (${devLabel}) ${stamp}.pdf`)

  if (failedSections.length) {
    console.warn(`PDF export: ${failedSections.length} section(s) could not be captured and were skipped`)
  }
}
